import { CONSTANTS, UPDATE_SOURCES, GET_SOURCES } from './constants';
import { NormalizedProduct, NormalizedLender } from './reducer';
import { ProductDealTerms, SecurityOfferedItem } from '../../modules/DebtProducts/debtProductTypes';

// Types for generateProductMetrics action
export interface ProductTermsPayload {
    maximumDealAmount?: number;
    maximumRoi?: number;
    minimumTenure?: number;
    security?: SecurityOfferedItem[];
}

export interface GenerateProductMetricsPayload {
    aggregatorId: string;
    orgId: string;
    productTerms: Record<string, Record<string, ProductTermsPayload>>;
}

export interface GenerateProductMetricsHandlers {
    onSuccess?: (data: any) => void;
    onError?: (error?: any) => void;
}

// Legacy action creators (for backward compatibility)
export const updateDebtProductsData = (source: string, data: any) => {
    return {
        type: CONSTANTS.UPDATE_DEBT_PRODUCTS_DATA,
        payload: {
            source,
            data,
        },
    };
};

export const getDebtProductsDataAction = (source: string, data: any) => {
    return {
        type: CONSTANTS.GET_DEBT_PRODUCTS_DATA,
        payload: {
            source,
            data,
        },
    };
};

// New optimized action creators for normalized state

/**
 * Update a single product by ID
 * Use this for single product updates (most efficient)
 */
export const updateProduct = (productId: string, updates: Partial<NormalizedProduct>) => ({
    type: CONSTANTS.UPDATE_PRODUCT,
    payload: { productId, updates },
});

/**
 * Update a single lender by lenderId and productId
 * Use this for single lender updates (most efficient)
 */
export const updateLender = (lenderId: string, productId: string, updates: Partial<NormalizedLender>) => ({
    type: CONSTANTS.UPDATE_LENDER,
    payload: { lenderId, productId, updates },
});

/**
 * Toggle product expand state
 * Optimized action for the most common update operation
 */
export const toggleProductExpand = (productId: string) => ({
    type: CONSTANTS.TOGGLE_PRODUCT_EXPAND,
    payload: { productId },
});

/**
 * Batch update multiple products
 * Use this when updating multiple products at once
 */
export const batchUpdateProducts = (updates: Array<{ productId: string; updates: Partial<NormalizedProduct> }>) => ({
    type: CONSTANTS.BATCH_UPDATE_PRODUCTS,
    payload: updates,
});

/**
 * Batch update multiple lenders
 * Use this when updating multiple lenders at once
 */
export const batchUpdateLenders = (updates: Array<{ lenderId: string; productId: string; updates: Partial<NormalizedLender> }>) => ({
    type: CONSTANTS.BATCH_UPDATE_LENDERS,
    payload: updates,
});

/**
 * Generate product metrics - calls API and updates redux with response
 * Used when editing product/lender fields (amount, tenure, roi)
 */
export const generateProductMetrics = (payload: GenerateProductMetricsPayload, handlers?: GenerateProductMetricsHandlers) => ({
    type: CONSTANTS.GENERATE_PRODUCT_METRICS,
    payload,
    handlers,
});

/**
 * Success action for generateProductMetrics
 * Dispatched by saga on successful API call
 */
export const generateProductMetricsSuccess = (data: any) => ({
    type: CONSTANTS.GENERATE_PRODUCT_METRICS_SUCCESS,
    payload: data,
});

/**
 * Failure action for generateProductMetrics
 * Dispatched by saga on API error
 */
export const generateProductMetricsFailure = (error: any) => ({
    type: CONSTANTS.GENERATE_PRODUCT_METRICS_FAILURE,
    payload: error,
});

/**
 * Restore debt products state to original values
 * Used when discarding changes to revert Redux state
 */
export const restoreDebtProductsState = (productsById: Record<string, any>, lendersById: Record<string, any>) => ({
    type: CONSTANTS.RESTORE_DEBT_PRODUCTS_STATE,
    payload: { productsById, lendersById },
});

export { UPDATE_SOURCES, GET_SOURCES };
